import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { FetchData } from "app/shared/generalMethods";
import { AdminProgramService } from "app/services/admin-program.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Location } from "@angular/common";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

@Component({
  selector: "app-organization",
  templateUrl: "./organization.component.html",
  styleUrls: ["./organization.component.scss"],
})
export class OrganizationComponent implements OnInit {
  isEditMode = false;
  pageLoader = false;
  adminData: any;
  orgForm!: FormGroup;
  imagePreviewOrg: string | null = null;
  isAdminLoggedIn = false;
  uploadProgress: number = 0;
  isUploading: boolean = false;

  constructor(
    private _fetchData: FetchData,
    private fb: FormBuilder,
    private adminProgramService: AdminProgramService,
    private _snackBar: MatSnackBar,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.loadUserData();
  }

  loadUserData(): void {
    const adminList = this._fetchData.data?.lstAdministratorData || [];
    if (adminList.length > 0) {
      this.isAdminLoggedIn = true;
      this.updateAdminData(adminList[0]);
    } else {
      this.isAdminLoggedIn = false;
      const staffList = this._fetchData.data?.lstStaffData || [];
      if (staffList.length > 0) {
        this.updateStaffData(staffList[0]);
      } else {
        console.warn("No admin or staff data found");
      }
    }
    this.findMatchingOrganization(this._fetchData.data?.adminEmailFirebase);
  }

  private findMatchingOrganization(adminEmail: string): any | null {
    const orgList = this._fetchData.data?.lstOrganizations || [];
    return (
      orgList.find(
        (org: any) =>
          org.Email_Address__c?.toLowerCase() === adminEmail?.toLowerCase()
      ) || null
    );
  }

  updateAdminData(admin: any): void {
    const adminEmail = admin?.Email__c;
    const matchingOrg = this.findMatchingOrganization(adminEmail);

    if (matchingOrg) {
      this.adminData = {
        OrganizationId: matchingOrg.Id,
        Name: matchingOrg.Name,
        OrganizationName: matchingOrg.Organization_Name__c,
        Phone: matchingOrg.Phone_Number__c,
        Address: matchingOrg.Organization_Address__c,
        OrgImage: this.extractCleanImageUrl(matchingOrg.Organization_Image__c),
      };
    } else {
      this.adminData = {
        OrganizationId: admin.Organization__r?.Id,
        Name: admin.Organization__r?.Name,
        OrganizationName: admin.Organization__r?.Organization_Name__c,
        Phone: admin.Organization__r?.Phone_Number__c,
        Address: admin.Organization__r?.Organization_Address__c,
        OrgImage: this.extractCleanImageUrl(
          admin.Organization__r?.Organization_Image__c
        ),
      };
    }

    this.imagePreviewOrg = this.adminData.OrgImage;
    this.initializeForm();
  }

  updateStaffData(staff: any): void {
    this.adminData = {
      OrganizationId: staff.Organization__r?.Id,
      Name: staff.Organization__r?.Name,
      OrganizationName: staff.Organization__r?.Organization_Name__c,
      Phone: staff.Organization__r?.Phone_Number__c,
      Address: staff.Organization__r?.Organization_Address__c,
      OrgImage: this.extractCleanImageUrl(
        staff.Organization__r?.Organization_Image__c
      ),
    };

    this.imagePreviewOrg = this.adminData.OrgImage;
    this.initializeForm();
  }

  extractCleanImageUrl(imageData: string): string {
    if (!imageData) return "";

    if (imageData.includes("<p>") || imageData.includes("</p>")) {
      let cleanedUrl = imageData.replace(/<p>/g, "").replace(/<\/p>/g, "");
      cleanedUrl = cleanedUrl.replace(/%3Cp%3E/g, "").replace(/%3C\/p%3E/g, "");
      cleanedUrl = cleanedUrl.replace(/&amp;/g, "&");
      cleanedUrl = cleanedUrl.trim();
      return cleanedUrl;
    }

    return imageData.replace(/&amp;/g, "&").trim();
  }

  initializeForm(): void {
    this.orgForm = this.fb.group({
      organizationId: [this.adminData.OrganizationId],
      name: [{ value: this.adminData.Name, disabled: true }],
      organizationName: [this.adminData.OrganizationName],
      phone: [this.adminData.Phone, Validators.required],
      address: [this.adminData.Address],
      orgImage: [this.adminData.OrgImage],
    });
  }

  toggleEditMode(): void {
    if (!this.isAdminLoggedIn) return;
    this.isEditMode = !this.isEditMode;

    if (!this.isEditMode) {
      this.resetFormToOriginalData();
    }
  }

  resetFormToOriginalData(): void {
    this.imagePreviewOrg = this.adminData.OrgImage;
    this.initializeForm();
  }

  onFormSubmit(): void {
    if (!this.isAdminLoggedIn) return;

    if (this.orgForm.valid) {
      this.pageLoader = true;

      const formData = this.prepareOrganizationData();
      const adminId = localStorage.getItem("adminId");

      if (this.isEditMode) {
        this.updateOrganization(formData, adminId!);
      }
    } else {
      this.openSnackBar("Please fill in all required fields correctly.");
      this.markFormGroupTouched(this.orgForm);
    }
  }

  markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  prepareOrganizationData(): any {
    const raw = this.orgForm.getRawValue();
    return {
      Id: raw.organizationId,
      Organization_Name__c: raw.organizationName,
      Phone_Number__c: raw.phone,
      Organization_Address__c: raw.address,
      Organization_Image__c: raw.orgImage,
    };
  }

  updateOrganization(formData: any, adminId: string): void {
    this.adminProgramService.updateOrganization(formData, adminId).subscribe(
      (response) => {
        const results: any = this._fetchData.parseIntoJSON(response.msg);

        if (results.success) {
          this.openSnackBar("Organization updated successfully!");
          this._fetchData.setLocalStorage(response.msg);
          this.refreshComponentData();
          this.isEditMode = false;
        } else {
          console.error("⚠️ Salesforce update failed:", results);
          this.openSnackBar("Error updating organization. Please try again.");
        }

        this.pageLoader = false;
      },
      (err) => {
        console.error("❌ Update Organization error:", err);
        this.pageLoader = false;
        this.openSnackBar("Error updating organization. Please try again.");
      }
    );
  }

  refreshComponentData(): void {
    try {
      const dataSet = localStorage.getItem("DataSet");
      if (dataSet) {
        const parsedData = JSON.parse(dataSet);
        this._fetchData.data = parsedData.data;
        this.loadUserData();
      }
    } catch (error) {
      console.error("Error refreshing component data:", error);
    }
  }

  openSnackBar(message: string): void {
    this._snackBar.open(message, "Close", {
      duration: 6000,
      horizontalPosition: "center",
      verticalPosition: "top",
    });
  }

  goBack(): void {
    this.location.back();
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) {
      console.warn("No file selected.");
      return;
    }

    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      this.openSnackBar(
        "Please select a valid image file (JPEG, PNG, GIF, WebP)"
      );
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.openSnackBar("Image size should be less than 5MB");
      return;
    }

    const storage = getStorage();
    const filePath = `organizationImages/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filePath);

    this.isUploading = true;
    this.uploadProgress = 0;

    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        this.uploadProgress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
      },
      (error) => {
        console.error("Firebase upload error:", error);
        this.isUploading = false;
        this.uploadProgress = 0;
        this.openSnackBar("Error uploading image. Please try again.");
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          this.imagePreviewOrg = downloadURL;
          this.orgForm.patchValue({ orgImage: downloadURL });
          this.isUploading = false;
          this.uploadProgress = 0;

          this.openSnackBar("Image uploaded successfully!");
        });
      }
    );
  }

  removeImage(): void {
    this.imagePreviewOrg = null;
    this.orgForm.patchValue({ orgImage: "" });
    this.openSnackBar("Image removed successfully!");
  }
}
